// use @under_construction.js @shared_header.js @dashboardfooter.js

// # Neo Mentee Step 1 Component
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, Routes, Route, useParams } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import sessionManager from '../../auth/session';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import ProtectedRouteUser from '../../routes/protected/protected_route_user';
import ProtectedRouteAdmin from '../../routes/protected/protected_route_admin';
import ProtectedRoute from '../../routes/ProtectedRoute';
import UWorldQBank from './qbanks1/uw1';
import AmbossQBank from './qbanks1/amboss1';
import NBMEQBank from './qbanks1/nbme1';
import Tasks1 from './tutora1/tasks1';
import MentorNotes1 from './tutora1/mentor_notes1';
import Checklist1 from './management1/checklist1';
import Dedicated1 from './management1/dedicated1';
import MenteeMeetings1 from './management1/mentee_meetings1';
import WeakSpots1 from './management1/weakspots1';
import Assignments1 from './assignments1/assignments1';
import MenteeStats1 from './M_stats1/mentee_stats1';
// # Import new components
import GiveAssignment1 from './assignments1/give_assign1';
import DedicatedTasks1 from './tutora1/give_dedic1';
import MenteeTasks1 from './mentoring1/mentee_tasks1';
// # Import missing components
import M1Calendar from './mentoring1/m1_calendar';
import NeoTime from '../common/neo_time';
import Counter1 from './mentoring1/counter1';
import Counter1Page from './mentoring1/counter1_page';
import TaskChapter1 from './tutora1/task_chapter1';
// # Import Firebase
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './neo_mentee_step1.css';
import cacheManager from '../../auth/cache_manager';

// # Import icons
import { 
  FaCalendarAlt, 
  FaClock,
  FaBook,
  FaClipboardCheck,
  FaChartLine,
  FaTasks,
  FaUserClock,
  FaListAlt,
  FaUserMd,
  FaExclamationTriangle,
  FaCalendarCheck,
  FaRegCircle,
  FaBookMedical,
  // # Import new icons
  FaQuestionCircle,
  FaFire,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaFlag,
  FaBrain
} from 'react-icons/fa';
import { GiNotebook } from 'react-icons/gi';
import { RiNumber100 } from 'react-icons/ri';

// # Import mentee icons
import calendarIcon from '../../aneo_media/mentee/calendar.png';
import timeIcon from '../../aneo_media/mentee/time.png';
import uworldIcon from '../../aneo_media/mentee/uworld.png';
import ambossIcon from '../../aneo_media/mentee/amboss.png';
import nbmeIcon from '../../aneo_media/mentee/NBME.png';
import counterIcon from '../../aneo_media/mentee/counter.png';
import assignmentIcon from '../../aneo_media/mentee/assignment.png';
import weaknessIcon from '../../aneo_media/mentee/weakness.png';
import dedicatedIcon from '../../aneo_media/mentee/dedicated.png';
import meetingIcon from '../../aneo_media/mentee/meeting.png';
import checklistIcon from '../../aneo_media/mentee/checklist.png';
import tasksIcon from '../../aneo_media/mentee/tasks.png';
import notesIcon from '../../aneo_media/mentee/notes.png';
import menteeCover from '../../aneo_media/mentee/mentee_cover.jpg';

// # Import components
import MenteeGuide from '../common/mentee_guide';

// # Mentor Card Component
const MentorCard = ({ userId }) => {
  const [mentorData, setMentorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mentorId, setMentorId] = useState(null);
  const [error, setError] = useState(null);

  // # Fetch mentee document to get mentor ID
  useEffect(() => {
    const fetchMentorId = async () => {
      try {
        if (!userId) {
          setLoading(false);
          return;
        }
        
        // # Get mentee document to find mentor ID
        const menteeDocRef = doc(db, 's1_mentee', userId);
        const menteeDoc = await getDoc(menteeDocRef);
        
        if (menteeDoc.exists()) {
          const menteeData = menteeDoc.data();
          // # Access currentMentor directly from mentorship object
          const currentMentorId = menteeData?.mentorship?.currentMentor;
          
          if (currentMentorId) {
            setMentorId(currentMentorId);
            // # Don't set loading to false here, wait for mentor data
          } else {
            console.log('No mentor ID found in mentorship data');
            setError('no_mentor');
            setLoading(false);
          }
        } else {
          console.log('No mentee document found');
          setError('no_mentee');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching mentor ID:', error);
        setError('fetch_error');
        setLoading(false);
      }
    };
    
    fetchMentorId();
  }, [userId]);

  // # Fetch mentor data once we have the mentor ID
  useEffect(() => {
    const fetchMentorData = async () => {
      try {
        if (!mentorId) return;
        
        // # Get mentor user document
        const mentorDocRef = doc(db, 'users', mentorId);
        const mentorDoc = await getDoc(mentorDocRef);
        
        if (mentorDoc.exists()) {
          const data = mentorDoc.data();
          if (!data) {
            throw new Error('Mentor document exists but has no data');
          }
          setMentorData(data);
        } else {
          console.log('Mentor document not found');
          setError('no_mentor_doc');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching mentor data:', error);
        setError('mentor_fetch_error');
        setLoading(false);
      }
    };
    
    if (mentorId) {
      fetchMentorData();
    }
  }, [mentorId]);

  // # Loading state
  if (loading) {
    return (
      <div className="neo_ment1_mentor_card">
        <div className="neo_ment1_mentor_header">
          <h3>My Mentor</h3>
        </div>
        <div className="neo_ment1_loading">
          <p>Loading mentor information...</p>
        </div>
      </div>
    );
  }

  // # Error or no mentor state
  if (!mentorData || error) {
    return (
      <div className="neo_ment1_mentor_card neo_ment1_no_mentor">
        <div className="neo_ment1_mentor_header">
          <h3>My Mentor</h3>
        </div>
        <div className="neo_ment1_no_mentor_content">
          <FaUserMd className="neo_ment1_no_mentor_icon" />
          <p>{error === 'fetch_error' ? 'Error loading mentor data' : 'No mentor assigned yet'}</p>
        </div>
      </div>
    );
  }

  // # Get profile photo URL or default to pfp.png
  const mentorPhotoUrl = mentorData?.photoURL || '/icons/pfp.png';

  return (
    <div className="neo_ment1_mentor_card">
      <div className="neo_ment1_mentor_header">
        <h3>My Mentor</h3>
      </div>
      <div className="neo_ment1_mentor_content">
        <div className="neo_ment1_mentor_avatar">
          <img 
            src={mentorPhotoUrl} 
            alt="Mentor" 
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/icons/pfp.png';
            }}
          />
        </div>
        <div className="neo_ment1_mentor_details">
          <h4>{mentorData.firstName || ''} {mentorData.middleName || ''} {mentorData.lastName || ''}</h4>
          
          <div className="neo_ment1_mentor_info_grid">
            <div className="neo_ment1_mentor_info_item">
              <FaUser className="neo_ment1_mentor_info_icon" />
              <span>{mentorData.gender ? mentorData.gender.charAt(0).toUpperCase() + mentorData.gender.slice(1) : 'N/A'}</span>
            </div>
            
            <div className="neo_ment1_mentor_info_item">
              <FaEnvelope className="neo_ment1_mentor_info_icon" />
              <span>{mentorData.email || 'N/A'}</span>
            </div>
            
            <div className="neo_ment1_mentor_info_item">
              <FaPhone className="neo_ment1_mentor_info_icon" />
              <span>
                {mentorData.selectedCountry?.code || ''} {mentorData.phoneNumber || 'N/A'}
              </span>
            </div>
            
            <div className="neo_ment1_mentor_info_item">
              <FaFlag className="neo_ment1_mentor_info_icon" />
              <span>{mentorData.nationality || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// # Add helper function to get the correct user ID for database operations
const getTargetUserId = (isAdmin, viewingMenteeId, currentUserId) => {
  // Always return mentee ID if we have one, otherwise return current user ID
  return viewingMenteeId || currentUserId;
};

// # Add helper function to get mentee data
const getMenteeData = async (menteeId) => {
  if (!menteeId) return null;
  
  try {
    const menteeRef = doc(db, 's1_mentee', menteeId);
    const menteeDoc = await getDoc(menteeRef);
    return menteeDoc.exists() ? menteeDoc.data() : null;
  } catch (error) {
    console.error('Error fetching mentee data:', error);
    return null;
  }
};

// # Add helper function to get mentee by username
const getMenteeByUsername = async (username) => {
  if (!username) return null;
  
  try {
    // # Query the users collection to find the user with the given username
    const usersCollection = collection(db, 'users');
    const q = query(usersCollection, where('username', '==', username));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userData = querySnapshot.docs[0].data();
      const userId = querySnapshot.docs[0].id;
      return { ...userData, uid: userId };
    }
    return null;
  } catch (error) {
    console.error('Error fetching mentee by username:', error);
    return null;
  }
};

// # Add helper function to update mentee data
const updateMenteeData = async (menteeId, data) => {
  if (!menteeId) return;
  
  try {
    const menteeRef = doc(db, 's1_mentee', menteeId);
    await setDoc(menteeRef, data, { merge: true });
  } catch (error) {
    console.error('Error updating mentee data:', error);
  }
};

function NeoMenteeStep1({ isAdmin, isMentorView }) {
  const navigate = useNavigate();
  const { userProfile, user, isTutor } = useAuthStore();
  const currentStep = 'step1';
  const { username } = useParams();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [viewingMenteeId, setViewingMenteeId] = useState(null);
  const [viewingMenteeData, setViewingMenteeData] = useState(null);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Listen for theme changes from DashboardHeader
  useEffect(() => {
    const handleThemeChange = (event) => {
      setIsDarkMode(event.detail.isDark);
    };
    
    window.addEventListener('theme_change', handleThemeChange);
    
    return () => {
      window.removeEventListener('theme_change', handleThemeChange);
    };
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Create base path based on admin/mentor status
  let basePath = '/mentorship';
  if (isAdmin) {
    basePath = '/admin/mentorship';
  }
  if (isMentorView && username) {
    basePath = `/mentor/${currentStep}/${username}`;
  }

  // # Fetch mentee data by username when in mentor view
  useEffect(() => {
    const fetchMenteeByUsername = async () => {
      if (isMentorView && username) {
        try {
          // # Query users collection for the username
          const usersCollection = collection(db, 'users');
          const q = query(usersCollection, where('username', '==', username));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            const userId = querySnapshot.docs[0].id;
            setViewingMenteeId(userId);
            setViewingMenteeData(userData);
            // # Store mentee ID in sessionStorage for other components
            sessionStorage.setItem('currentViewingMenteeId', userId);
          } else {
            console.error('No user found with username:', username);
            // # If no user found, redirect to dashboard
            navigate(isTutor ? '/tutor' : '/dashboard');
          }
        } catch (error) {
          console.error('Error fetching mentee by username:', error);
        }
      }
    };
    
    fetchMenteeByUsername();
  }, [isMentorView, username, navigate, isTutor]);

  // # Check if admin/tutor is viewing a specific mentee
  useEffect(() => {
    if ((isAdmin || isTutor) && !isMentorView) {
      // # Get the source of navigation from sessionStorage
      const navigationSource = sessionStorage.getItem('menteeNavigationSource');
      const menteeId = sessionStorage.getItem('currentViewingMenteeId');
      
      // # If coming from mentees_list.js, use the mentee ID
      if (navigationSource === 'mentees_list' && menteeId) {
        setViewingMenteeId(menteeId);
        
        // # Fetch basic mentee data to display in the UI
        const fetchMenteeData = async () => {
          try {
            const menteeDocRef = doc(db, 'users', menteeId);
            const menteeDoc = await getDoc(menteeDocRef);
            
            if (menteeDoc.exists()) {
              setViewingMenteeData(menteeDoc.data());
            }
          } catch (error) {
            console.error('Error fetching mentee data:', error);
          }
        };
        
        fetchMenteeData();
      } else if (navigationSource === 'admin_dashboard' || navigationSource === 'tutor_dashboard') {
        // # If coming from admin/tutor dashboard, clear any existing mentee ID
        setViewingMenteeId(null);
        setViewingMenteeData(null);
        sessionStorage.removeItem('currentViewingMenteeId');
      } else if (menteeId) {
        // # For backward compatibility, handle existing code path
        setViewingMenteeId(menteeId);
        
        // # Fetch basic mentee data to display in the UI
        const fetchMenteeData = async () => {
          try {
            const menteeDocRef = doc(db, 'users', menteeId);
            const menteeDoc = await getDoc(menteeDocRef);
            
            if (menteeDoc.exists()) {
              setViewingMenteeData(menteeDoc.data());
            }
          } catch (error) {
            console.error('Error fetching mentee data:', error);
          }
        };
        
        fetchMenteeData();
      }
    }
  }, [isAdmin, isTutor, isMentorView]);

  // # No need for subscription check here as it's handled by ProtectedRouteUser
  useEffect(() => {
    if (!user?.uid && !viewingMenteeId && !isMentorView) {
      navigate('/signin');
    }
  }, [user, navigate, viewingMenteeId, isMentorView]);

  // # Group dashboard items into sections
  const dashboardSections = {
    time: {
      title: 'Time & Stats',
      items: [
        {
          title: 'My Tasks',
          icon: <FaTasks className="neo_ment1_react_icon" style={{ color: '#FF9800' }} />,
          path: isMentorView ? `mentoring1/tasks` : `${basePath}/${currentStep}/mentoring1/tasks`
        },
        {
          title: 'Tasks Calendar',
          icon: calendarIcon,
          path: isMentorView ? `calendar` : `${basePath}/${currentStep}/calendar`
        },
        {
          title: 'Meetings',
          icon: meetingIcon,
          path: isMentorView ? `meetings` : `${basePath}/${currentStep}/meetings`
        },
        {
          title: 'Time Tracking',
          icon: timeIcon,
          path: isMentorView ? `time` : `${basePath}/${currentStep}/time`
        },
        {
          title: 'Mentee Brain',
          icon: <FaBrain className="neo_ment1_react_icon" style={{ color: '#4CAF50' }} />,
          path: isMentorView ? `stats` : `${basePath}/${currentStep}/stats`
        }
      ]
    },
    qbanks: {
      title: 'Question Banks & Resources',
      items: [
        {
          title: 'UWORLD',
          icon: uworldIcon,
          path: isMentorView ? `uworld` : `${basePath}/${currentStep}/uworld`
        },
        {
          title: 'Amboss',
          icon: ambossIcon,
          path: isMentorView ? `amboss` : `${basePath}/${currentStep}/amboss`
        },
        {
          title: 'NBME',
          icon: nbmeIcon,
          path: isMentorView ? `nbme` : `${basePath}/${currentStep}/nbme`
        },
        {
          title: 'Mistake Counter',
          icon: counterIcon,
          path: isMentorView ? `mentoring1/counter1` : `${basePath}/${currentStep}/mentoring1/counter1`
        },
        {
          title: 'Mentee Guide',
          icon: <FaBookMedical className="neo_ment1_react_icon" style={{ color: '#2196F3' }} />,
          path: isMentorView ? `mentee-guide` : `${basePath}/${currentStep}/mentee-guide`
        }
      ]
    },
    management: {
      title: 'Management',
      items: [
        {
          title: 'Assignments',
          icon: assignmentIcon,
          path: isMentorView ? `assignments` : `${basePath}/${currentStep}/assignments`
        },
        {
          title: 'Weak Spots',
          icon: weaknessIcon,
          path: isMentorView ? `weakspots` : `${basePath}/${currentStep}/weakspots`
        },
        {
          title: 'Dedicated Plan',
          icon: dedicatedIcon,
          path: isMentorView ? `plan` : `${basePath}/${currentStep}/plan`
        },
        {
          title: 'Checklist',
          icon: checklistIcon,
          path: isMentorView ? `checklist` : `${basePath}/${currentStep}/checklist`
        }
      ]
    },
    mentor: {
      title: 'Mentor Section',
      items: [
        {
          title: 'Assign Tasks',
          icon: tasksIcon,
          path: isMentorView ? `tasks` : `${basePath}/${currentStep}/tasks`
        },
        {
          title: 'Mentor Notes',
          icon: notesIcon,
          path: isMentorView ? `notes` : `${basePath}/${currentStep}/notes`
        },
        // # Add new items
        {
          title: 'Give Assignment',
          icon: <FaQuestionCircle className="neo_ment1_react_icon" style={{ color: '#FF5722' }} />,
          path: isMentorView ? `give-assignment` : `${basePath}/${currentStep}/give-assignment`
        },
        {
          title: 'Dedicated Tasks',
          icon: <FaFire className="neo_ment1_react_icon" style={{ color: '#FF9800' }} />,
          path: isMentorView ? `dedicated-tasks` : `${basePath}/${currentStep}/dedicated-tasks`
        }
      ]
    }
  };

  // # Get profile photo URL or default to pfp.png
  const profilePhotoUrl = isAdmin && viewingMenteeData 
    ? viewingMenteeData?.photoURL || '/icons/pfp.png'
    : userProfile?.photoURL || '/icons/pfp.png';

  return (
    <div className={`neo_ment1_container ${isDarkMode ? 'neo_ment1_dark-mode' : ''}`}>
      <Routes>
        {/* # Main dashboard view */}
        <Route 
          path="/" 
          element={
            <ProtectedRouteUser requiredSubscription="step1_mentorship">
              <>
                <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
                {/* # If tutor is viewing mentee, we need to allow access */}
                {((isAdmin || isTutor) && viewingMenteeData) ? (
                  <div className="neo_ment1_profile_section">
                    <div className="neo_ment1_cover_image">
                      <img src={menteeCover} alt="Cover" />
                    </div>
                    {/* # Step Indicator */}
                    <div className="neo_ment1_step_indicator">
                      <FaRegCircle className="neo_ment1_step_icon" />
                      <span>STEP 1</span>
                    </div>
                    <div className="neo_ment1_profile_info">
                      <div className="neo_ment1_avatar">
                        <img 
                          src={profilePhotoUrl} 
                          alt="Profile"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/icons/pfp.png';
                          }}
                        />
                      </div>
                      <div className="neo_ment1_details">
                        {(isAdmin || isTutor) && viewingMenteeData ? (
                          <>
                            <h2>
                              {viewingMenteeData?.firstName} {viewingMenteeData?.middleName} {viewingMenteeData?.lastName}
                              <span className="neo_ment1_viewing_badge">
                                Viewing as {isTutor ? 'Tutor' : 'Mentor'}
                              </span>
                            </h2>
                            <p>@{viewingMenteeData?.username}</p>
                          </>
                        ) : (
                          <>
                            <h2>{userProfile?.firstName} {userProfile?.middleName} {userProfile?.lastName}</h2>
                            <p>@{userProfile?.username}</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="neo_ment1_profile_section">
                    <div className="neo_ment1_cover_image">
                      <img src={menteeCover} alt="Cover" />
                    </div>
                    {/* # Step Indicator */}
                    <div className="neo_ment1_step_indicator">
                      <FaRegCircle className="neo_ment1_step_icon" />
                      <span>STEP 1</span>
                    </div>
                    <div className="neo_ment1_profile_info">
                      <div className="neo_ment1_avatar">
                        <img 
                          src={profilePhotoUrl} 
                          alt="Profile"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/icons/pfp.png';
                          }}
                        />
                      </div>
                      <div className="neo_ment1_details">
                        {(isAdmin || isTutor) && viewingMenteeData ? (
                          <>
                            <h2>
                              {viewingMenteeData?.firstName} {viewingMenteeData?.middleName} {viewingMenteeData?.lastName}
                              <span className="neo_ment1_viewing_badge">
                                Viewing as {isTutor ? 'Tutor' : 'Mentor'}
                              </span>
                            </h2>
                            <p>@{viewingMenteeData?.username}</p>
                          </>
                        ) : (
                          <>
                            <h2>{userProfile?.firstName} {userProfile?.middleName} {userProfile?.lastName}</h2>
                            <p>@{userProfile?.username}</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="neo_ment1_dashboard_sections">
                  {/* # Add Mentor Card Section */}
                  <div className="neo_ment1_section neo_ment1_mentor_section">
                    {isMentorView ? (
                      // # When in mentor view, show card with current user (mentor) data
                      <div className="neo_ment1_mentor_card">
                        <div className="neo_ment1_mentor_header">
                          <h3>Mentor Profile</h3>
                        </div>
                        <div className="neo_ment1_mentor_content">
                          <div className="neo_ment1_mentor_avatar">
                            <img 
                              src={userProfile?.photoURL || '/icons/pfp.png'} 
                              alt="Mentor" 
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/icons/pfp.png';
                              }}
                            />
                          </div>
                          <div className="neo_ment1_mentor_details">
                            <h4>{userProfile?.firstName} {userProfile?.middleName} {userProfile?.lastName}</h4>
                            
                            <div className="neo_ment1_mentor_info_grid">
                              <div className="neo_ment1_mentor_info_item">
                                <FaUser className="neo_ment1_mentor_info_icon" />
                                <span>{userProfile?.gender ? userProfile.gender.charAt(0).toUpperCase() + userProfile.gender.slice(1) : 'N/A'}</span>
                              </div>
                              
                              <div className="neo_ment1_mentor_info_item">
                                <FaEnvelope className="neo_ment1_mentor_info_icon" />
                                <span>{userProfile?.email || 'N/A'}</span>
                              </div>
                              
                              <div className="neo_ment1_mentor_info_item">
                                <FaPhone className="neo_ment1_mentor_info_icon" />
                                <span>{userProfile?.selectedCountry?.code || ''} {userProfile?.phoneNumber || 'N/A'}</span>
                              </div>
                              
                              <div className="neo_ment1_mentor_info_item">
                                <FaFlag className="neo_ment1_mentor_info_icon" />
                                <span>{userProfile?.nationality || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // # When not in mentor view, show normal mentee's assigned mentor card
                      <MentorCard userId={isAdmin && viewingMenteeId ? viewingMenteeId : user?.uid} />
                    )}
                  </div>
                  
                  {Object.entries(dashboardSections).map(([key, section]) => (
                    // # Only show mentor section to admins and tutors, show all other sections to everyone
                    (key === 'mentor' && !isAdmin && !isTutor) ? null : (
                      <div key={key} className="neo_ment1_section">
                        <h3 className="neo_ment1_section_title">{section.title}</h3>
                        <div className="neo_ment1_section_grid">
                          {section.items.map((item, index) => (
                            <Link 
                              key={index} 
                              to={item.path}
                              className="neo_ment1_dashboard_item"
                            >
                              <div className="neo_ment1_item_icon">
                                {typeof item.icon === 'string' ? (
                                  <img src={item.icon} alt={item.title} />
                                ) : (
                                  item.icon
                                )}
                              </div>
                              <h3>{item.title}</h3>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>

                <DashboardFooter isDarkMode={isDarkMode} />
              </>
            </ProtectedRouteUser>
          } 
        />

        {/* # Stats route */}
        <Route 
          path="stats" 
          element={
            isAdmin || isTutor ? (
              <MenteeStats1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <MenteeStats1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        {/* # QBank routes - Protected by ProtectedRouteUser for users */}
        <Route 
          path="uworld" 
          element={
            isAdmin || isTutor ? (
              <UWorldQBank isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <UWorldQBank isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        <Route 
          path="amboss" 
          element={
            isAdmin || isTutor ? (
              <AmbossQBank isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <AmbossQBank isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        <Route 
          path="nbme" 
          element={
            isAdmin || isTutor ? (
              <NBMEQBank isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <NBMEQBank isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        {/* # Management routes */}
        <Route 
          path="tasks" 
          element={
            isAdmin || isTutor ? (
              <Tasks1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <Tasks1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        <Route 
          path="notes" 
          element={
            isAdmin || isTutor ? (
              <MentorNotes1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <MentorNotes1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        <Route 
          path="checklist" 
          element={
            isAdmin || isTutor ? (
              <Checklist1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <Checklist1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        <Route 
          path="plan" 
          element={
            isAdmin || isTutor ? (
              <Dedicated1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <Dedicated1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        <Route 
          path="meetings" 
          element={
            isAdmin || isTutor ? (
              <MenteeMeetings1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <MenteeMeetings1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        <Route 
          path="weakspots" 
          element={
            isAdmin || isTutor ? (
              <WeakSpots1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <WeakSpots1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        <Route 
          path="assignments" 
          element={
            isAdmin || isTutor ? (
              <Assignments1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <Assignments1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />

        {/* # Add new routes */}
        <Route 
          path="give-assignment" 
          element={
            isAdmin || isTutor ? (
              <ProtectedRoute requiredRole={isAdmin ? "admin" : "tutor"}>
                <GiveAssignment1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
              </ProtectedRoute>
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <GiveAssignment1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        <Route 
          path="dedicated-tasks" 
          element={
            isAdmin || isTutor ? (
              <ProtectedRoute requiredRole={isAdmin ? "admin" : "tutor"}>
                <DedicatedTasks1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
              </ProtectedRoute>
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <DedicatedTasks1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
        
        {/* # Add Mentee Guide route */}
        <Route 
          path="mentee-guide" 
          element={
            isAdmin || isTutor ? (
              <MenteeGuide isAdmin={isAdmin} isTutor={isTutor} stepType="1" menteeId={viewingMenteeId} />
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <MenteeGuide isAdmin={isAdmin} stepType="1" />
              </ProtectedRouteUser>
            )
          } 
        />

        {/* # Add Calendar route */}
        <Route 
          path="calendar" 
          element={
            isAdmin || isTutor ? (
              <ProtectedRoute requiredRole={isAdmin ? "admin" : "tutor"}>
                <M1Calendar isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
              </ProtectedRoute>
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <M1Calendar isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />

        {/* # Add Time route */}
        <Route 
          path="time" 
          element={
            isAdmin || isTutor ? (
              <ProtectedRoute requiredRole={isAdmin ? "admin" : "tutor"}>
                <NeoTime stepType="Step 1" isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
              </ProtectedRoute>
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <NeoTime stepType="Step 1" />
              </ProtectedRouteUser>
            )
          } 
        />

        {/* # Add Counter routes */}
        <Route 
          path="mentoring1/counter1" 
          element={
            isAdmin || isTutor ? (
              <ProtectedRoute requiredRole={isAdmin ? "admin" : "tutor"}>
                <Counter1 isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
              </ProtectedRoute>
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <Counter1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />

        <Route 
          path="mentoring1/counter1/:chapterId" 
          element={
            isAdmin || isTutor ? (
              <ProtectedRoute requiredRole={isAdmin ? "admin" : "tutor"}>
                <Counter1Page isAdmin={isAdmin} isTutor={isTutor} menteeId={viewingMenteeId} />
              </ProtectedRoute>
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <Counter1Page isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />

        {/* # Add Task Chapter route */}
        <Route 
          path="tutora1/chapter/:chapterId" 
          element={
            isAdmin || isTutor ? (
              <ProtectedRoute requiredRole={isAdmin ? "admin" : "tutor"}>
                <TaskChapter1 
                  isAdmin={isAdmin} 
                  isTutor={isTutor} 
                  isMentorView={true} 
                  menteeId={viewingMenteeId} 
                />
              </ProtectedRoute>
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <TaskChapter1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />

        {/* # Add Tasks route */}
        <Route 
          path="mentoring1/tasks" 
          element={
            isAdmin || isTutor ? (
              <ProtectedRoute requiredRole={isAdmin ? "admin" : "tutor"}>
                <MenteeTasks1 
                  isAdmin={isAdmin} 
                  isTutor={isTutor} 
                  isMentorView={true} 
                  menteeId={viewingMenteeId} 
                />
              </ProtectedRoute>
            ) : (
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <MenteeTasks1 isAdmin={isAdmin} />
              </ProtectedRouteUser>
            )
          } 
        />
      </Routes>
    </div>
  );
}

export default NeoMenteeStep1;